import styles from '../PromoSummary.module.scss';

import cx from 'classnames';
import React, { FC } from 'react';
import { useTranslation } from 'react-i18next';

import { Image } from 'components/Image/Image';
import { TextV2 } from 'components/TextV2/TextV2';
import closeIcon from 'images/icons/close-black.svg';
import { ContentType, PromoSummary } from 'api/contentful/contentTypes.cloverdotcom';
import { useSingleContentfulEntry } from 'hooks/useSingleContentfulEntry';
import { useValidatedProgramPromotion } from 'hooks/ProgramPromotion/useValidatedProgramPromotion';
import {
  initialFetchingPromoValue,
  useFetchingPromoInternal,
} from '../FetchingPromoContext/useFetchingPromoInternal';
import { APPLIED_PROMO_TEST_ID } from './AppliedPromo.const';
import { useCartContext } from 'components/Cart/hooks/useCartContext';

interface IAppliedPromoProps {
  handleRemovePromo?: () => void;
  disabledXButton?: boolean;
}

export const AppliedPromo: FC<IAppliedPromoProps> = ({ handleRemovePromo, disabledXButton }) => {
  const { t } = useTranslation();
  const {
    checkout: { promo: promoInCart },
    removePromo,
  } = useCartContext();
  const { programPromotion } = useValidatedProgramPromotion();
  const { fetchingPromo, setFetchingPromo } = useFetchingPromoInternal();
  const { data: promoEntry, isLoading } = useSingleContentfulEntry<PromoSummary>(ContentType.PROMO_SUMMARY);
  const { title, blurb, enableCustomPromoCopy } = promoEntry || {};

  const activePromo = fetchingPromo.data || programPromotion;
  const endOfDayOffsetInMilliseconds = 3600000 * 23 + 59 * 60000;
  const hasProgramPromoApplied = promoInCart.applied && promoInCart.applied === promoInCart.program;

  const removeAppliedPromo = () => {
    removePromo();
    setFetchingPromo(initialFetchingPromoValue);
    if (handleRemovePromo) handleRemovePromo();
  };

  if (isLoading) return null;

  return (
    <div
      className={cx(styles.promoSummaryBox, { [styles.promoSummaryBoxApplied]: activePromo })}
      data-testid={APPLIED_PROMO_TEST_ID.box}
      aria-live="polite"
      role="alert"
    >
      {!hasProgramPromoApplied && !disabledXButton && (
        <button
          aria-label={t('promo-code.remove-promo-code-icon-alt-text')}
          className={styles.promoSummaryCloseButton}
          onClick={removeAppliedPromo}
          data-testid={APPLIED_PROMO_TEST_ID.remove}
        >
          <Image src={closeIcon} role="presentation" />
        </button>
      )}
      <TextV2
        tagName="h4"
        variant="body-medium"
        value={(enableCustomPromoCopy && title) || t('promo-code.title', { code: promoInCart.applied })}
        data-testid={APPLIED_PROMO_TEST_ID.title}
      />
      {activePromo && (
        <TextV2
          tagName="p"
          variant="body-small"
          value={
            (enableCustomPromoCopy && blurb) ||
            t('promo-code.description', {
              date: new Date(
                Date.parse(activePromo.endDate) + endOfDayOffsetInMilliseconds,
              ).toLocaleDateString(),
              description: activePromo.promotionLongDescription,
            })
          }
          className={styles.promoSummaryDescription}
          data-testid={APPLIED_PROMO_TEST_ID.description}
        />
      )}
    </div>
  );
};
